import { z } from 'zod';
import { Cents } from './common.js';

const Version = z.int().positive();
const Reason = z.string().trim().min(1).max(2000);
const StockValue = Cents.refine((value) => value >= 0);
const ChoiceFields = [
  z.object({ choice: z.literal('a') }).strict(),
  z.object({ choice: z.literal('b') }).strict(),
  z.object({ choice: z.literal('c') }).strict(),
  z.object({ choice: z.literal('d'), stockValueCents: StockValue }).strict(),
] as const;

/** The server supplies every amount, tax estimate, deadline and identity used in calculation. */
export const ElectionSelection = z.discriminatedUnion('choice', ChoiceFields);
export type ElectionSelection = z.infer<typeof ElectionSelection>;
export const SaveElection = z.discriminatedUnion('choice', [
  ChoiceFields[0].extend({ version: Version }),
  ChoiceFields[1].extend({ version: Version }),
  ChoiceFields[2].extend({ version: Version }),
  ChoiceFields[3].extend({ version: Version }),
]);
export type SaveElection = z.infer<typeof SaveElection>;
export const CloseElectionAsCash = z.object({ version: Version, reason: Reason }).strict();
export type CloseElectionAsCash = z.infer<typeof CloseElectionAsCash>;
export const ExtendElection = z
  .object({
    version: Version,
    deadlineDate: z.iso.date().refine((value) => !value.startsWith('0000-')),
    reason: Reason,
  })
  .strict();
export type ExtendElection = z.infer<typeof ExtendElection>;
export const ReleaseAwardEarly = z.object({ version: Version }).strict();
export type ReleaseAwardEarly = z.infer<typeof ReleaseAwardEarly>;

export const ElectionEstimate = z.object({
  lineId: z.uuid(),
  version: Version,
  choice: z.enum(['a', 'b', 'c', 'd']),
  deadline: z.object({ date: z.iso.date(), at: z.iso.datetime({ offset: true }) }),
  shares: z.int().nonnegative(),
  stockValueCents: StockValue,
  cashCents: StockValue,
  estimatedTaxCents: StockValue,
  manualTaxCents: StockValue,
  maxStockValueCents: StockValue,
  maxShares: z.int().nonnegative(),
  estimate: z.literal(true),
});
export type ElectionEstimate = z.infer<typeof ElectionEstimate>;

/** Command receipts expose the plain stage; My rewards provides the employee's full view. */
export const ElectionSaved = z.object({
  lineId: z.uuid(),
  version: Version,
  stage: z.enum(['employee_choice', 'paid', 'closed', 'with_stock_admin']),
});
export type ElectionSaved = z.infer<typeof ElectionSaved>;

// --- Past the deadline (SA4, APP-067) -----------------------------------------------------------

const Person = z.object({ id: z.uuid(), name: z.string() });
const Instant = z.iso.datetime({ offset: true });

/**
 * `POST /api/v1/awards/:requestId/lines/:lineId/election/chase`: email the employee again, now,
 * that their choice is past its deadline. The Stock Admin team only, a PAA line past its deadline
 * only, once per person per day in the organization's time zone (409 `already_chased`). It's an
 * election reminder (N4 #15) marked as chased; the line stays past its deadline.
 */
export const ChaseElection = z
  .object({ version: Version, note: z.string().trim().min(1).max(500).optional() })
  .strict();
export type ChaseElection = z.infer<typeof ChaseElection>;

export const ElectionChased = z.object({
  lineId: z.uuid(),
  version: Version,
  chasedAt: Instant,
  /** When the next chase is allowed: the start of the next day in the organization's zone. */
  nextChaseFrom: Instant,
});
export type ElectionChased = z.infer<typeof ElectionChased>;

/** One contact about a late election, oldest first: a reminder, a chase, an extension, a nudge. */
export const ElectionContact = z.object({
  at: Instant,
  kind: z.enum(['reminder', 'chase', 'extended', 'manager_nudge']),
  /** The Stock Admin who chased or extended; null for scheduled emails. */
  by: Person.nullable(),
  /** Who the email went to; null for an extension. */
  to: Person.nullable(),
  /** The note with a chase, or the reason for an extension. */
  note: z.string().nullable(),
});
export type ElectionContact = z.infer<typeof ElectionContact>;

/** One person past their election deadline (SA4): what the Stock Admin needs to follow up. */
export const PastDeadlineElection = z.object({
  lineId: z.uuid(),
  award: z.object({ id: z.uuid(), displayId: z.string(), type: z.literal('PAA') }),
  /** The award's version, sent with Extend, Close as all cash and Chase. */
  version: Version,
  person: z.object({
    id: z.uuid(),
    name: z.string(),
    eeNumber: z.string().nullable(),
    homeOrg: z.string().nullable(),
  }),
  sector: z.object({ id: z.string(), name: z.string() }).nullable(),
  manager: Person.nullable(),
  cashCents: Cents,
  options: z.int().nonnegative(),
  /** When the letter went to its holder. */
  letterDate: Instant.nullable(),
  /** The deadline in force, an extended one included, in the organization's calendar. */
  deadline: z.object({ date: z.iso.date(), at: Instant }),
  daysSinceLetter: z.int().nonnegative().nullable(),
  daysLate: z.int().nonnegative(),
  contacts: z.array(ElectionContact),
  /** The manager follow-up setting for this award type, and the last one sent. */
  managerNudge: z.object({
    enabled: z.boolean(),
    daysLate: z.int().nonnegative(),
    sentAt: Instant.nullable(),
    to: Person.nullable(),
  }),
  /** The choice-(a) tax estimate saved at the check, for the close-as-cash summary. */
  estimateACents: Cents.nullable(),
  /** The last chase, and whether one was sent today (one a day). */
  lastChasedAt: Instant.nullable(),
  chasedToday: z.boolean(),
  actions: z.object({ chase: z.boolean(), extend: z.boolean(), closeAsCash: z.boolean() }),
});
export type PastDeadlineElection = z.infer<typeof PastDeadlineElection>;

/** `GET /api/v1/elections/past-deadline`: everyone past their deadline, latest deadline last. */
export const PastDeadlineElections = z.object({
  asOf: z.iso.date(),
  items: z.array(PastDeadlineElection),
});
export type PastDeadlineElections = z.infer<typeof PastDeadlineElections>;
